import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  useBodyOverflowHidden,
  useViewportSize,
} from '@ole-ui/ole-ui-components';

import { aiAssistantContext } from 'components/Course/AiAssist/store';
import AiAssistSidebar from './AiAssistSidebar';

/**
 * Measures the bottom edge of the sticky navigation bar so the overlay
 * can start below it. The masthead is sticky at top: 0, so its bottom edge
 * always equals its height regardless of scroll position.
 */
function measureNavBottom(): number {
  const masthead = document.querySelector<HTMLElement>('.pf-v5-c-masthead');
  if (masthead) return masthead.getBoundingClientRect().bottom;
  return 0;
}

/**
 * Mobile (phones + tablets) AI Assistant overlay.
 *
 * Triggered by the bottom HUD CTA ("Get recommendations").
 * Uses the same sidebar content component as desktop, but rendered full-screen.
 */
const AiAssistMobileOverlay: React.FC = () => {
  const aiAssistantStore = useContext(aiAssistantContext);
  const { isLowerThanLg: isMobileMode } = useViewportSize();
  const [navBottom, setNavBottom] = useState(0);

  const isOpen = Boolean(aiAssistantStore?.isOpen);
  useBodyOverflowHidden(isMobileMode && isOpen);

  // Keep the overlay top in sync with the nav bar height, re-measuring on
  // resize (e.g. orientation changes) so it never overlaps the masthead.
  useEffect(() => {
    if (!isMobileMode || !isOpen) return undefined;
    const update = () => setNavBottom(measureNavBottom());
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isMobileMode, isOpen]);

  if (!aiAssistantStore || !isMobileMode || !isOpen) return null;

  return (
    <div
      aria-label="Learning Assistant"
      aria-modal="true"
      className="hud-course-view__ai-assist-mobile-overlay pf-v5-theme-dark"
      role="dialog"
      style={navBottom > 0 ? { top: navBottom } : undefined}
    >
      <div className="hud-course-view__ai-assist-mobile-panel">
        <AiAssistSidebar aiAssistantStore={aiAssistantStore} />
      </div>
    </div>
  );
};

export default observer(AiAssistMobileOverlay);
